import { useRef, useState } from 'react';
import { getDomain, updateDomain } from '@/services/domainRegistry';
import { normalizePhaseApplicability } from '@/services/domain/phaseApplicability';
import PhaseApplicabilityField from './PhaseApplicabilityField';

export default function DomainPhaseSettings({ domain, schoolId }) {
  const [draft, setDraft] = useState(null);
  const [acknowledged, setAcknowledged] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const saving = useRef(false);
  const current = acknowledged && acknowledged.governance.configurationRevision >= (domain.governance?.configurationRevision || 0)
    ? acknowledged : domain;
  const value = draft?.value ?? current.phaseApplicability;
  const save = async event => {
    event.preventDefault();
    if (!draft || saving.current) return;
    saving.current = true;
    setStatus('saving'); setError('');
    try {
      const phaseApplicability = normalizePhaseApplicability(draft.value);
      const result = await updateDomain(schoolId, domain.id, { phaseApplicability }, { expectedConfigurationRevision: draft.revision });
      setAcknowledged({ ...current, phaseApplicability,
        governance: { ...current.governance, configurationRevision: result.configurationRevision } });
      setDraft(null); setStatus('saved');
    } catch (failure) {
      setError(['domain/configuration-conflict', 'functions/aborted'].includes(failure.code)
        ? 'This Domain changed in another session. Your edits are still here. Reload saved settings, review them, then make your changes again.'
        : failure.message || 'Phase settings could not be saved. Your edits are still here.'); setStatus('error');
    } finally { saving.current = false; }
  };
  const reload = async () => {
    if (saving.current) return;
    saving.current = true; setStatus('saving');
    try {
      const saved = await getDomain(schoolId, domain.id);
      if (!saved) throw new Error('This Domain is no longer available.');
      setAcknowledged(saved); setDraft(null); setError(''); setStatus('');
    } catch (failure) { setError(failure.message); setStatus('error'); }
    finally { saving.current = false; }
  };
  return <form onSubmit={save} className="space-y-3 rounded-xl border border-base-300 p-4">
    <h3 className="font-semibold">Phase participation</h3>
    <PhaseApplicabilityField schoolId={schoolId} value={value} disabled={status === 'saving'} onChange={value => {
      setDraft(previous => ({ value, revision: previous?.revision ?? current.governance?.configurationRevision ?? 0 }));
      setStatus(''); setError('');
    }} />
    <p className="text-xs text-base-content/65">Phase settings take effect after the school’s phase processes are reviewed and activated.</p>
    {error && <div role="alert" className="space-y-2 text-sm text-error"><p>{error}</p>
      <button type="button" className="btn btn-ghost btn-sm" disabled={status === 'saving'} onClick={reload}>Reload saved settings (discard edits)</button>
    </div>}
    {status === 'saved' && <p role="status" className="text-sm text-success">Phase participation saved.</p>}
    <div className="flex justify-end gap-2">
      {draft && <button type="button" className="btn btn-ghost btn-sm" disabled={status === 'saving'} onClick={() => { setDraft(null); setError(''); setStatus(''); }}>Cancel</button>}
      <button type="submit" className="btn btn-primary btn-sm" disabled={!draft || status === 'saving'}>{status === 'saving' ? 'Saving…' : 'Save phase participation'}</button>
    </div>
  </form>;
}
